'use client';

import { useEffect, useRef, useState } from 'react';
import { sendFeedback } from '@/lib/api';
import Layer from './Layer';

/**
 * "Send feedback" — the box for the one thing the product cannot measure.
 *
 * Everything else about a translation is already recorded: how long it took,
 * which mode was clicked, whether the answer came from cache. None of it can
 * say the answer was *wrong*, or rude, or in the wrong register — that is
 * knowledge only the person reading it has, and this is the only place they can
 * put it.
 *
 * Three decisions, each of them the difference between a form that gets filled
 * in and one that does not:
 *
 * **The message is the only required field.** Not the email, not the face. A
 * feedback form that asks for an address first is asking someone to identify
 * themselves before they have decided whether to bother, and most of them stop
 * there. The address is offered as "if you want an answer", which is the only
 * reason a sender has to give one.
 *
 * **The two panes are attached only if ticked.** "The translation is too formal" is
 * unusable without the translation; it is also somebody's private message, and
 * shipping it with a complaint they never agreed to file would make this the
 * one control on the page that collects text nobody offered. So the box is
 * shown, pre-ticked when there is something to attach, with the actual texts
 * visible underneath it — a ticked box is only consent if it is obvious what it
 * covers.
 *
 * **The typing survives a failure.** The send is the one action here that can
 * fail, and the thing being lost is a paragraph somebody wrote by hand. The
 * error is shown above the box, with every word still in it and the button
 * ready to try again.
 */
export default function FeedbackDialog({
  onClose,
  context,
}: {
  onClose: () => void;
  /** The page as it stands, offered to the sender rather than taken from them. */
  context: {
    sourceLang: string | null;
    targetLang: string | null;
    sourceLangName: string | null;
    targetLangName: string | null;
    mode: string | null;
    modeLabel: string | null;
    sourceText: string;
    targetText: string;
  };
}) {
  const [message, setMessage] = useState('');
  const [sentiment, setSentiment] = useState<'good' | 'bad' | null>(null);
  const [email, setEmail] = useState('');
  const hasContext = !!(context.sourceText.trim() || context.targetText.trim());
  const [attach, setAttach] = useState(hasContext);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const panel = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  // Escape closes, the caret starts in the box, and the page behind stops
  // scrolling under the dialog — the same three things VoiceDialog does, because
  // a modal that behaves differently from the other modal on the same page is
  // a modal people have to learn twice.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    box.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The thank-you is a state of this dialog rather than a second one, and it
  // closes itself: there is nothing left to do here, and a panel that waits to
  // be dismissed after the work is done is one more click for no information.
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(onClose, 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  async function submit() {
    const text = message.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      await sendFeedback({
        message: text,
        sentiment,
        email: email.trim() || null,
        page: typeof window === 'undefined' ? null : window.location.pathname,
        ...(attach && hasContext
          ? {
              sourceLang: context.sourceLang,
              targetLang: context.targetLang,
              mode: context.mode,
              sourceText: context.sourceText,
              targetText: context.targetText,
            }
          : {}),
      });
      setSent(true);
    } catch (err) {
      setError((err as Error).message || 'Could not send that.');
    } finally {
      setSending(false);
    }
  }

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Send feedback"
    >
      <div
        ref={panel}
        tabIndex={-1}
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-ink-200
                   bg-white shadow-2xl outline-none dark:border-ink-700 dark:bg-ink-900"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-ink-200 px-5 py-4 dark:border-ink-800">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-200/60
                           text-accent-700 dark:bg-accent-700/35 dark:text-accent-300">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.4 8.4 0 01-9 8.4 9.4 9.4 0 01-3-.5L3 21l1.6-4.2A8.2 8.2 0 013 11.5 8.5 8.5 0 0112 3a8.5 8.5 0 019 8.5z" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Send feedback</h2>
            <p className="truncate text-[11px] text-ink-500 dark:text-ink-400">
              A wrong translation, a missing language, anything that got in your way.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-2 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700
                       dark:hover:bg-ink-800 dark:hover:text-ink-100"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {sent ? (
          /* Deliberately the whole panel rather than a line under the form: the
             message is gone from the box, and a form still sitting there with a
             note above it reads as though it might need sending again. */
          <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <p className="text-sm font-medium text-ink-900 dark:text-ink-50">Thank you — it arrived.</p>
            <p className="max-w-sm text-xs leading-relaxed text-ink-500 dark:text-ink-400">
              Every one of these is read.
              {email.trim() ? ' If it needs an answer, it will go to the address you left.' : ''}
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
            {error && (
              <p role="alert" className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700
                                         dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </p>
            )}

            {/*
              Optional, and two faces rather than five stars: the only thing a
              scale would add here is the illusion of measurement over a handful
              of messages a week.

              Each face carries its own colour BEFORE it is pressed — a happy
              amber one and a sad red one — rather than two grey outlines that
              only differ by the curve of a 6-pixel mouth. Two identical grey
              circles ask the reader to study them; two coloured faces are read
              at a glance, which is all the attention this control deserves on
              the way to the box that actually matters.

              Amber for happy because that is what a smiling face is everywhere
              it is drawn, and because green is already spoken for in this app —
              it means "done" (the copied tick, a translated page), and a green
              face here would read as a state rather than as an opinion. Red for
              sad, which needs no explaining.

              Pressing one fills it in: the tint becomes the whole button and the
              face turns white. Colour alone would not survive a screenshot in
              greyscale, so the fill and the ring carry the state as well.
            */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-ink-500 dark:text-ink-400">How was it?</span>
              {([
                {
                  key: 'good' as const,
                  label: 'Good',
                  // Corners of the mouth turned up, and eyes to match.
                  mouth: 'M8 14.2c1 1.4 2.4 2.1 4 2.1s3-.7 4-2.1',
                  on: 'border-amber-500 bg-amber-500 text-white shadow-sm hover:bg-amber-600',
                  off:
                    'border-amber-300 bg-amber-50 text-amber-600 hover:border-amber-400 hover:bg-amber-100 ' +
                    'dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20',
                },
                {
                  key: 'bad' as const,
                  label: 'Not good',
                  mouth: 'M8 16.4c1-1.4 2.4-2.1 4-2.1s3 .7 4 2.1',
                  on: 'border-red-500 bg-red-500 text-white shadow-sm hover:bg-red-600',
                  off:
                    'border-red-300 bg-red-50 text-red-600 hover:border-red-400 hover:bg-red-100 ' +
                    'dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20',
                },
              ]).map((f) => {
                const on = sentiment === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={on}
                    aria-label={f.label}
                    onClick={() => setSentiment((s) => (s === f.key ? null : f.key))}
                    title={on ? `${f.label} — press again to unset` : f.label}
                    className={
                      'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ' +
                      'transition-all duration-150 hover:-translate-y-0.5 active:translate-y-0 ' +
                      'motion-reduce:transform-none ' +
                      (on ? f.on : f.off)
                    }
                  >
                    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0" fill="none"
                         stroke="currentColor" strokeWidth="1.8">
                      <circle cx="12" cy="12" r="9" />
                      {/* Dots rather than strokes: at 18px an eye drawn as a line
                          is a frown of its own. */}
                      <path d="M9 9.8h.01M15 9.8h.01" strokeLinecap="round" strokeWidth="2.4" />
                      <path d={f.mouth} strokeLinecap="round" />
                    </svg>
                    {f.label}
                  </button>
                );
              })}
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-700 dark:text-ink-200">
                What happened?
              </span>
              <textarea
                ref={box}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 4000))}
                rows={5}
                placeholder="The translation came out too formal for a message to a friend…"
                className="w-full resize-y rounded-xl border border-ink-200 bg-white px-3 py-2.5 text-sm
                           leading-relaxed text-ink-900 outline-none transition placeholder:text-ink-400
                           focus:border-accent-500 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-700 dark:text-ink-200">
                Email <span className="font-normal text-ink-400">— only if you want an answer</span>
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900
                           outline-none transition placeholder:text-ink-400 focus:border-accent-500
                           dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              />
            </label>

            {hasContext && (
              <div className="rounded-xl border border-ink-200 p-3 dark:border-ink-700">
                <label className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={attach}
                    onChange={(e) => setAttach(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-accent-600"
                  />
                  <span className="text-xs leading-relaxed text-ink-700 dark:text-ink-200">
                    Send what is on screen with this — the two texts below,{' '}
                    {context.sourceLangName && context.targetLangName
                      ? `${context.sourceLangName} → ${context.targetLangName}`
                      : 'the language pair'}
                    {context.modeLabel ? `, ${context.modeLabel}` : ''}. Without it there is
                    nothing to look at.
                  </span>
                </label>
                {/* The texts themselves, not a promise about them. A tick box is
                    only consent when what it covers is on screen beside it. */}
                <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
                  {[
                    { key: 'src', label: context.sourceLangName ?? 'Your text', text: context.sourceText },
                    { key: 'dst', label: context.targetLangName ?? 'Translation', text: context.targetText },
                  ].map((p) => (
                    <div
                      key={p.key}
                      className={
                        'rounded-lg bg-ink-100 p-2.5 transition dark:bg-ink-800 ' +
                        (attach ? '' : 'opacity-40')
                      }
                    >
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                        {p.label}
                      </p>
                      <p className="line-clamp-4 whitespace-pre-wrap text-[12px] leading-relaxed text-ink-600 dark:text-ink-300">
                        {p.text.trim() || '—'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!sent && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-ink-200 px-5 py-3.5
                          dark:border-ink-800">
            <span className="text-[11px] text-ink-400">
              {message.trim().length ? `${message.trim().length} / 4,000` : 'Nothing is sent until you press send.'}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-3 py-2 text-sm font-medium text-ink-500 transition hover:bg-ink-100
                           hover:text-ink-800 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!message.trim() || sending}
                className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition
                           hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-40
                           disabled:hover:bg-accent-600"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </Layer>
  );
}
